import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Draft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  jobTitle: string;
  linkedinUrl: string;
  notes: string;
};

const EMPTY_DRAFT: Draft = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  mobilePhone: "",
  jobTitle: "",
  linkedinUrl: "",
  notes: "",
};

export type ContactFormInitial = Partial<Draft> & { id?: string };

export function ContactFormDialog({
  open,
  onOpenChange,
  mode,
  clientId,
  initial,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  clientId?: string | null;
  initial?: ContactFormInitial;
  onSaved?: (contactId: string) => void;
}) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  useEffect(() => {
    if (!open) return;
    setDraft({
      firstName: initial?.firstName ?? "",
      lastName: initial?.lastName ?? "",
      email: initial?.email ?? "",
      phone: initial?.phone ?? "",
      mobilePhone: initial?.mobilePhone ?? "",
      jobTitle: initial?.jobTitle ?? "",
      linkedinUrl: initial?.linkedinUrl ?? "",
      notes: initial?.notes ?? "",
    });
  }, [open, initial]);

  const invalidate = () => {
    utils.contacts.list.invalidate();
    if (clientId) utils.contacts.listByClient.invalidate({ clientId });
    if (initial?.id) utils.contacts.get.invalidate({ id: initial.id });
  };

  const create = trpc.contacts.create.useMutation({
    onSuccess: (row) => {
      toast.success("Contact created");
      invalidate();
      onOpenChange(false);
      onSaved?.(row.id);
    },
    onError: (err) => toast.error(err.message),
  });

  const update = trpc.contacts.update.useMutation({
    onSuccess: (row) => {
      toast.success("Contact updated");
      invalidate();
      onOpenChange(false);
      onSaved?.(row.id);
    },
    onError: (err) => toast.error(err.message),
  });

  const busy = create.isPending || update.isPending;

  const handleSave = () => {
    const payload = {
      firstName: draft.firstName.trim() || null,
      lastName: draft.lastName.trim() || null,
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
      mobilePhone: draft.mobilePhone.trim() || null,
      jobTitle: draft.jobTitle.trim() || null,
      linkedinUrl: draft.linkedinUrl.trim() || null,
      notes: draft.notes.trim() || null,
    };

    if (!payload.firstName && !payload.lastName && !payload.email) {
      toast.error("Need at least a first name, last name or email.");
      return;
    }

    if (mode === "create") {
      create.mutate({ ...payload, clientId: clientId ?? null });
    } else if (initial?.id) {
      update.mutate({ id: initial.id, patch: payload });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New contact" : "Edit contact"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Fill in at least a first name, last name or email."
              : "Update contact details."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="First name"
            value={draft.firstName}
            onChange={(v) => setDraft({ ...draft, firstName: v })}
            autoFocus
          />
          <Field
            label="Last name"
            value={draft.lastName}
            onChange={(v) => setDraft({ ...draft, lastName: v })}
          />
          <Field
            label="Email"
            type="email"
            value={draft.email}
            onChange={(v) => setDraft({ ...draft, email: v })}
          />
          <Field
            label="Role / title"
            value={draft.jobTitle}
            onChange={(v) => setDraft({ ...draft, jobTitle: v })}
          />
          <Field
            label="Phone"
            type="tel"
            value={draft.phone}
            onChange={(v) => setDraft({ ...draft, phone: v })}
          />
          <Field
            label="Mobile phone"
            type="tel"
            value={draft.mobilePhone}
            onChange={(v) => setDraft({ ...draft, mobilePhone: v })}
          />
          <div className="sm:col-span-2">
            <Field
              label="LinkedIn"
              type="url"
              value={draft.linkedinUrl}
              onChange={(v) => setDraft({ ...draft, linkedinUrl: v })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground">
              Notes
            </Label>
            <Textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={busy}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {mode === "create" ? "Create contact" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "tel" | "url";
  autoFocus?: boolean;
}) {
  return (
    <div>
      <Label className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
        autoFocus={autoFocus}
      />
    </div>
  );
}
